/**
 * Quick Actions' "become an expert" walkthrough is session-durable via
 * localStorage today. There's no backend endpoint for it yet — once one
 * exists this module is the one place to swap the storage, everything
 * else (the palette UI) only calls the functions below.
 */

export type WalkthroughTaskId =
  | "opened-with-shortcut"
  | "jumped-to-something"
  | "searched-by-typing"
  | "asked-ai"
  | "hid-a-workspace"
  | "visited-every-center";

const PROGRESS_KEY = "ig-quick-actions-progress";
const VISITED_CENTERS_KEY = "ig-quick-actions-visited-centers";

/** Kept in sync with CENTERS.length in centers.tsx — there's no shared
 * import here to avoid a circular dependency between the two modules. */
const TOTAL_CENTERS = 5;

function readSet(key: string): Set<string> {
  try {
    const raw = localStorage.getItem(key);
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
  } catch {
    return new Set();
  }
}

function writeSet(key: string, value: Set<string>) {
  try {
    localStorage.setItem(key, JSON.stringify(Array.from(value)));
  } catch {
    /* storage is a convenience, not a requirement */
  }
}

export function getCompletedTasks(): Set<WalkthroughTaskId> {
  return readSet(PROGRESS_KEY) as Set<WalkthroughTaskId>;
}

/** Returns true when this call is what newly completed the task. */
export function markTaskDone(taskId: WalkthroughTaskId): boolean {
  const done = getCompletedTasks();
  if (done.has(taskId)) return false;
  done.add(taskId);
  writeSet(PROGRESS_KEY, done);
  return true;
}

export function getVisitedCenters(): Set<string> {
  return readSet(VISITED_CENTERS_KEY);
}

/** Call whenever the user lands on a Center's home or one of its
 * workspaces. Returns true when this visit completed the "visited every
 * Center" task. */
export function recordCenterVisit(centerId: string): boolean {
  const visited = getVisitedCenters();
  if (visited.has(centerId)) return false;
  visited.add(centerId);
  writeSet(VISITED_CENTERS_KEY, visited);
  if (visited.size >= TOTAL_CENTERS) {
    return markTaskDone("visited-every-center");
  }
  return false;
}
